import { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * Messages for the validation errors Angular raises itself, which rocket-ui does not register.
 *
 * Formly's `FormlyValidationMessage` walks the control's errors and **returns on the first key**,
 * whatever that key resolves to - including `undefined`. An error carrying its own `message` supplies
 * one (which is how every custom-field validator in `@shared/validators` reports), but Angular's
 * built-in `pattern` and `maxlength` errors carry only `requiredPattern`/`actualValue` and
 * `requiredLength`/`actualLength`. With nothing registered for those keys the field rendered as
 * invalid with no message at all, and because the loop returns on that first key, a second error
 * that did have a message - `minlength`, say - never got the chance to speak either.
 *
 * On a custom field that made the step unfinishable: the customer saw a red box, no reason for it,
 * and a Continue that silently refused to advance (KOR-14964).
 *
 * A field that wants to say something more specific still can: `field.validation.messages` and a
 * validator's own `message` both take precedence over what is registered here.
 */

/**
 * Deliberately generic. A custom field's regex is authored by the business as a bare pattern with no
 * human description attached - KOR stores the expression and nothing else - so there is nothing
 * truer to say than that the value does not match. Help text is where a business explains the shape.
 */
export const patternValidationMessage = (): string => 'Enter a value in the required format';

/** Worded as rocket-ui's `minLengthValidationMessage` is, so the pair reads as one rule. */
export const maxLengthValidationMessage = (_error: unknown, field: FormlyFieldConfig): string =>
  `Must be no more than ${field.props?.maxLength} characters long`;
